/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import BenchmarkScorePopoverContent from './BenchmarkScorePopoverContent';
import type { ProgressBarProps } from './ProgressBar';
import ProgressBar from './ProgressBar';
import { ProgressTrackerHeader, ProgressTrackerHeaderPlaceholder } from './ProgressTrackerHeader';

const styles = {
  container: css`
    width: 100%;
  `,
};

type ProgressTrackerProps = ProgressBarProps & {
  /** Name of the skill being tracked */
  skillName?: string;
  /** Size variant for the header */
  headerSize?: 'sm' | 'md';
  /** Whether to hide the score display */
  hideScore?: boolean;
  /** Whether to hide the target indicator */
  hideTarget?: boolean;
  /** Custom label text for personal score */
  personalScoreLabelText?: string;
  /** Custom label text for benchmark score */
  benchmarkLabelText?: string;
  /** Custom content for personal score popover */
  personalScorePopoverContent?: JSX.Element;
  /** Additional element to render after personal score */
  afterPersonalScore?: JSX.Element;
  /** Additional element to render at the end of header */
  endOfHeader?: JSX.Element;
  /** Whether the tracker is being used for scoring purposes */
  forScoring?: boolean;
  /** Array of segment values for progress visualization */
  segments?: number[];
  /** Whether to wrap the header content */
  wrapHeader?: boolean;
  /** Custom tooltip for the score */
  scoreTooltip?: React.ReactNode;
  /** Child elements to render */
  children?: JSX.Element;
};

type PropsForPlaceholder = Pick<ProgressTrackerProps, 'headerSize'>;

/**
 * A component that tracks and displays progress with a header and progress bar
 *
 * @component
 * @param {ProgressTrackerProps} props - Component props
 * @returns {JSX.Element} A progress tracking interface with header and progress bar
 */
const ProgressTracker = (props: ProgressTrackerProps): JSX.Element => {
  const { skillName, headerSize, hideScore = false, hideTarget, target, children, ...rest } = props;

  const benchmarkTooltip = <BenchmarkScorePopoverContent skillName={skillName} target={target} score={props.score} />;

  return (
    <div css={styles.container} className="rc-ProgressTracker">
      <ProgressTrackerHeader
        skillName={skillName}
        headerSize={headerSize}
        hideScore={hideScore}
        hideTarget={hideTarget}
        target={target}
        {...rest}
      />
      {children}
      <ProgressBar targetTooltip={benchmarkTooltip} target={!hideTarget ? target : undefined} {...rest} />
    </div>
  );
};

/**
 * Placeholder version of ProgressTracker for loading states
 *
 * @component
 * @param {PropsForPlaceholder} props - Props for placeholder
 * @param {('sm'|'md')} [props.headerSize] - Size variant for the header
 * @returns {JSX.Element} A placeholder version of the progress tracker
 */
const Placeholder = ({ headerSize }: PropsForPlaceholder): JSX.Element => (
  <div className="rc-ProgressTracker">
    <ProgressTrackerHeaderPlaceholder headerSize={headerSize} />
    <ProgressBar.Placeholder />
  </div>
);

Placeholder.displayName = 'ProgressTracker.Placeholder';
ProgressTracker.Placeholder = Placeholder;
ProgressTracker.displayName = 'ProgressTracker';

export default ProgressTracker;
